import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CLOSE_KINDS, Issue, IssueAge, IssueGroup, IssueInvestigation, IssueList, IssueRef, SweeperStatus } from '../../api/issues';
import { STATUS_LABEL } from '../../api/report';
import { TablePager } from '../Table/TablePager';
import { Refreshing } from '../Loading/Loading';
import { IdChip } from '../Money/IdText';
import { IssueDetail } from './IssueDetail';
import { age, ago, date, dateAgo, dateTime } from './issueFormat';

const PAGE_SIZES = [50, 100, 250];

/** The object types whose id is Exact's own: what comes from Exact on the way to Chargebee. */
const EXACT_SIDE = ['Payment', 'PaymentSalesEntry', 'Chargeback'];

/** How many examples of one kind of problem go to the skill: enough to see the pattern. */
const SKILL_SAMPLES = 5;

export interface IIssueBacklogProps {
    list: IssueList;
    activeCode: string | null;
    onCode: (code: string | null) => void;
    /** Whether the list is being fetched again, after a filter changed. */
    loading?: boolean;
    busy?: boolean;
    /** Whether the list is of one environment: then the column with the environment is left out. */
    oneEnvironment?: boolean;
    /** Closes issues after asking why. */
    onClose?: (items: IssueRef[], what: string) => void;
    onReopen?: (items: IssueRef[]) => void;
    /** Opens "Relatie kiezen": the customer next to the Exact accounts, to relink. */
    onRelink?: (issue: Issue) => void;
    /** Opens "Koppelen": the document next to the booking Exact has of it already. */
    onLink?: (issue: Issue) => void;
    /** Goes to the mail to the customer of an issue's environment. */
    onMail?: (issue: Issue) => void;
    /** Which ages the list shows, and the limit in days between recent and backlog. */
    age?: IssueAge;
    maxAgeDays?: number;
    /**
     * Closes every open issue of a kind of problem on the server, also the ones the list is
     * too short to show; only the ones older than the age limit when the list shows the backlog.
     */
    onCloseMatching?: (group: IssueGroup, onlyBacklog: boolean) => void;
    /** Starts or ends looking into a kind of problem. */
    onInvestigate?: (group: IssueGroup, start: boolean) => void;
    /** How the scanner stands: whether it offers anything again by itself, and whether this API sends for real. */
    scanner?: SweeperStatus | null;
}

const isOpen = (issue: Issue) => issue.status !== 'Resolved' && issue.status !== 'Closed';

const refOf = (issue: Issue): IssueRef => ({ customer: issue.customer, environment: issue.environment, key: issue.key });

type Next = 'us' | 'customer' | 'requeue' | 'auto' | 'done';

const NEXT: Record<Next, { label: string; icon: 'gear' | 'user' | 'rotate-left' | 'rotate' | 'check'; tone: string }> = {
    us: { label: 'Wij', icon: 'gear', tone: 'support' },
    customer: { label: 'Klant', icon: 'user', tone: 'customer' },
    requeue: { label: 'Opnieuw', icon: 'rotate-left', tone: 'automatic' },
    auto: { label: 'Vanzelf', icon: 'rotate', tone: 'automatic' },
    done: { label: 'Klaar', icon: 'check', tone: 'none' },
};

/** Who has to act now on a kind of problem: whoever the most urgent open issue waits on. */
const nextOf = (g: IssueGroup): Next =>
    g.open === 0 ? 'done' : g.waitingOnUs > 0 ? 'us' : g.waitingOnCustomer > 0 ? 'customer' : g.needsRequeue > 0 ? 'requeue' : 'auto';

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('nl-NL')} ${n === 1 ? one : many}`;

/** "3 bij ons, 47 opnieuw aanbieden": where the open issues of a kind of problem stand. */
const breakdown = (g: IssueGroup): string =>
    [
        g.waitingOnUs > 0 ? `${g.waitingOnUs} bij ons` : '',
        g.waitingOnCustomer > 0 ? `${g.waitingOnCustomer} bij de klant` : '',
        g.needsRequeue > 0 ? `${g.needsRequeue} opnieuw aanbieden` : '',
        g.resolvesItself > 0 ? `${g.resolvesItself} gaat vanzelf` : '',
    ]
        .filter(Boolean)
        .join(', ');

const share = (part: number, whole: number) => (whole === 0 ? 0 : Math.round((100 * part) / whole));

/**
 * What the skill c2e-research-error needs to improve the rule behind a known problem: the
 * rule as it reads now, what became of the issues, and a few real examples of what it was given.
 */
const forSkill = (group: IssueGroup, items: Issue[]) => {
    const samples = items.filter((i) => i.code === group.code);

    return {
        request: `Onderzoek het probleem '${group.code}' met de skill c2e-research-error: betere uitleg, de juiste eigenaar of een automatische controle.`,
        code: group.code,
        title: group.title,
        owner: group.owner,
        open: group.open,
        openByStatus: {
            waitingOnUs: group.waitingOnUs,
            waitingOnCustomer: group.waitingOnCustomer,
            needsRequeue: group.needsRequeue,
            resolvesItself: group.resolvesItself,
        },
        resolved: group.resolved,
        attempts: group.attempts,
        environments: group.environments,
        firstFailedAt: group.firstFailedAt,
        lastFailedAt: group.lastFailedAt,
        outcomes: group.outcomes,
        investigation: group.investigation ?? null,
        interpretation: samples.find((i) => i.interpretation)?.interpretation ?? null,
        samples: samples.slice(0, SKILL_SAMPLES).map((i) => ({
            customer: i.customer,
            environment: i.environment,
            objectType: i.objectType,
            eventType: i.eventType,
            relationId: i.relationId,
            chargebeeCustomerId: i.chargebeeCustomerId,
            invoiceId: i.invoiceId,
            exactState: i.exactState,
            status: i.status,
            attempts: i.attempts,
            firstFailedAt: i.firstFailedAt,
            lastFailedAt: i.lastFailedAt,
            errorType: i.errorType,
            errorMessage: i.errorMessage,
            location: i.location,
            checks: i.checks,
            closedKind: i.closedKind,
            closedReason: i.closedReason,
        })),
    };
};

/**
 * Every problem in the list with its examples, for the skill to go through and pick what it can
 * explain better or automate.
 */
const reviewForSkill = (list: IssueList) => ({
    request:
        'Loop deze problemen na met de skill c2e-research-error: welke regels kunnen een betere uitleg, de juiste eigenaar of een automatische controle krijgen? Kijk ook naar de outcomes: wat lost opnieuw aanbieden op, wat helpt een klantmail, wat wordt met de hand gesloten?',
    exportedAt: new Date().toISOString(),
    since: list.since,
    truncated: list.truncated,
    problems: [...list.groups].sort((a, b) => b.open - a.open).map((group) => forSkill(group, list.items)),
});

/**
 * The backlog itself: per kind of problem how many are open, who has to act and how old they
 * are, and underneath every object, the open ones first. A row opens to what the error means,
 * the error as the connector wrote it, and the way to the dossier.
 */
export const IssueBacklog = ({
    list,
    activeCode,
    onCode,
    loading,
    busy,
    oneEnvironment,
    onClose,
    onReopen,
    onRelink,
    onLink,
    onMail,
    age: shownAge,
    maxAgeDays,
    onCloseMatching,
    onInvestigate,
    scanner,
}: IIssueBacklogProps) => {
    const [expanded, setExpanded] = useState<string | null>(null);
    // The kind of problem just clicked, until the list for it is in.
    const [pending, setPending] = useState<string | null>(null);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[1]);
    const [copied, setCopied] = useState<string | null>(null);

    useEffect(() => {
        if (!loading) {
            setPending(null);
        }
    }, [loading]);

    useEffect(() => setPage(0), [list]);

    const choose = (code: string | null) => {
        setPending(code ?? '');
        onCode(code);
    };

    const pageCount = Math.max(1, Math.ceil(list.items.length / pageSize));
    const current = Math.min(page, pageCount - 1);
    const rows = useMemo(() => list.items.slice(current * pageSize, current * pageSize + pageSize), [list.items, current, pageSize]);

    const active = list.groups.find((g) => g.code === activeCode) ?? null;
    const activeOpen = active ? list.items.filter((i) => i.code === active.code && isOpen(i)) : [];

    const download = () => {
        const blob = new Blob([JSON.stringify(reviewForSkill(list), null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `issues-voor-skill-${new Date().toISOString().slice(0, 10)}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const copy = (group: IssueGroup) =>
        navigator.clipboard
            ?.writeText(JSON.stringify(forSkill(group, list.items), null, 2))
            .then(() => {
                setCopied(group.code);
                window.setTimeout(() => setCopied(null), 2500);
            })
            .catch(() => setCopied(null));

    const investigate = (group: IssueGroup) => {
        copy(group);
        onInvestigate?.(group, true);
    };

    const closeGroup = () => {
        if (!active || active.open === 0) {
            return;
        }

        // On the server: the list may be cut short, the group counts them all.
        if (onCloseMatching) {
            onCloseMatching(active, shownAge === 'backlog');
            return;
        }

        if (onClose && activeOpen.length > 0) {
            onClose(activeOpen.map(refOf), `${plural(activeOpen.length, 'issue', 'issues')} van "${active.title}" sluiten`);
        }
    };

    return (
        <>
            <section className="issues__section" aria-label="Per soort probleem">
                <div className="issues__notice-head">
                    <h3 className="issues__heading">
                        <FontAwesomeIcon icon="layer-group" /> Per soort probleem
                    </h3>
                    {list.groups.length > 0 && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={download}
                            title="Alle soorten problemen met voorbeelden als JSON. Geef het bestand in Claude Code aan de skill c2e-research-error."
                        >
                            <FontAwesomeIcon icon="cloud-arrow-down" /> Exporteren voor de skill
                        </button>
                    )}
                </div>
                {list.groups.length === 0 ? (
                    <p className="issues__muted">
                        <FontAwesomeIcon icon="circle-check" /> Niets gevonden met deze filters.
                    </p>
                ) : (
                    <Refreshing active={!!loading} label="Bijwerken…">
                        <div className="problem-list" aria-label="Soorten problemen">
                            <div className="problem-list__head" aria-hidden="true">
                                <span>Aan zet</span>
                                <span>Probleem</span>
                                <span className="problem-list__num">
                                    Open
                                </span>
                                <span className="problem-list__num" title="Eerste fout in de laatste 7 dagen">
                                    Nieuw
                                </span>
                                <span>Laatste fout</span>
                                <span>Oudste open</span>
                            </div>
                            {list.groups.map((group) => (
                                <GroupRow
                                    key={group.code}
                                    group={group}
                                    active={group.code === activeCode}
                                    dimmed={!!activeCode && group.code !== activeCode}
                                    pending={pending === group.code || (pending === '' && group.code === activeCode)}
                                    onClick={() => choose(group.code === activeCode ? null : group.code)}
                                />
                            ))}
                        </div>
                    </Refreshing>
                )}
                {active ? (
                    <GroupActions
                        group={active}
                        busy={busy}
                        copied={copied === active.code}
                        backlog={shownAge === 'backlog'}
                        maxAgeDays={maxAgeDays ?? 30}
                        onCopy={() => copy(active)}
                        onInvestigate={onInvestigate ? () => investigate(active) : undefined}
                        onStopInvestigation={onInvestigate ? () => onInvestigate(active, false) : undefined}
                        onClose={(onCloseMatching || onClose) && active.open > 0 ? closeGroup : undefined}
                    />
                ) : (
                    list.groups.length > 0 && <p className="issues__muted">Klik een probleem om de issues te zien, te sluiten of te laten onderzoeken.</p>
                )}
            </section>

            <section className="issues__section" aria-label="Issues">
                <h3 className="issues__heading">
                    <FontAwesomeIcon icon="list-ul" /> Issues
                    {!list.itemsOmitted && <span className="issues__heading-count">{list.total.toLocaleString('nl-NL')}</span>}
                </h3>
                {list.itemsOmitted ? (
                    <p className="issues__hint">
                        <FontAwesomeIcon icon="circle-info" /> Kies hierboven een probleem om de issues te zien. Over alle klanten zijn het er{' '}
                        {list.total.toLocaleString('nl-NL')}. Of open een klant via Omgevingen.
                    </p>
                ) : (
                    <>
                        <TablePager
                            firstOnPage={rows.length === 0 ? 0 : current * pageSize + 1}
                            lastOnPage={current * pageSize + rows.length}
                            total={list.total}
                            page={current}
                            pageCount={pageCount}
                            pageSize={pageSize}
                            pageSizes={PAGE_SIZES}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => {
                                setPageSize(size);
                                setPage(0);
                            }}
                            rowNoun="issues"
                            note={list.truncated ? 'ingekort' : undefined}
                            hint={list.truncated ? `De lijst toont ${list.items.length} van de ${list.total}. Filter verder om de rest te zien.` : undefined}
                        />
                        <Refreshing active={!!loading} label="Issues ophalen…">
                            <div className="issues__scroll">
                                <table className="issues__table">
                                    <thead>
                                        <tr>
                                            <th>Status</th>
                                            {!oneEnvironment && <th>Klant / omgeving</th>}
                                            <th>Object</th>
                                            <th>Probleem</th>
                                            <th className="issues__num">Pogingen</th>
                                            <th>Eerste fout</th>
                                            <th>Laatste fout</th>
                                            <th>Open sinds / opgelost</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((issue) => {
                                            const id = `${issue.customer}/${issue.environment}/${issue.key}`;
                                            const shown = expanded === id;
                                            const group = list.groups.find((g) => g.code === issue.code);

                                            return (
                                                <IssueRows
                                                    key={id}
                                                    issue={issue}
                                                    open={shown}
                                                    busy={busy}
                                                    oneEnvironment={oneEnvironment}
                                                    onToggle={() => setExpanded(shown ? null : id)}
                                                    onClose={onClose}
                                                    onReopen={onReopen}
                                                    onRelink={onRelink}
                                                    onLink={onLink}
                                                    onMail={onMail}
                                                    copied={copied === issue.code}
                                                    onCopy={group ? () => copy(group) : undefined}
                                                    investigation={group?.investigation}
                                                    onInvestigate={onInvestigate && group ? () => investigate(group) : undefined}
                                                    scanner={scanner}
                                                />
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Refreshing>
                    </>
                )}
            </section>
        </>
    );
};

const NextBadge = ({ group }: { group: IssueGroup }) => {
    if (group.unrecognised && group.open > 0) {
        return (
            <span className="owner-badge owner-badge--support" title={`Nog geen regel kent deze fout. ${breakdown(group)}`}>
                <FontAwesomeIcon icon="circle-question" />
                <span>Onbekend</span>
            </span>
        );
    }

    const next = NEXT[nextOf(group)];

    return (
        <span className={`owner-badge owner-badge--${next.tone}`} title={breakdown(group) || 'Niets meer open'}>
            <FontAwesomeIcon icon={next.icon} />
            <span>{next.label}</span>
        </span>
    );
};

const GroupRow = ({
    group,
    active,
    dimmed,
    pending,
    onClick,
}: {
    group: IssueGroup;
    active: boolean;
    dimmed: boolean;
    pending: boolean;
    onClick: () => void;
}) => (
    <button
        type="button"
        className={`problem-list__row${active ? ' is-active' : ''}${dimmed ? ' is-dimmed' : ''}`}
        onClick={onClick}
        aria-pressed={active}
        title={active ? 'Toon weer alle soorten' : 'Toon alleen de issues van dit probleem'}
    >
        <span className="problem-list__next">
            {pending ? <FontAwesomeIcon icon="spinner" spin className="issues__group-check" /> : active && <FontAwesomeIcon icon="check" className="issues__group-check" />}
            <NextBadge group={group} />
        </span>
        <span className="problem-list__what">
            <span className={group.unrecognised ? 'issues__pattern' : 'problem-list__title'}>{group.title}</span>
            <span className="problem-list__where">
                {group.environments.length === 1 ? group.environments[0] : `${group.environments.length} omgevingen`}
                {group.investigation && (
                    <span className="problem-list__investigating" title={group.investigation.note ?? undefined}>
                        <FontAwesomeIcon icon="magnifying-glass" /> In onderzoek bij {group.investigation.startedBy ?? 'iemand'}
                    </span>
                )}
            </span>
        </span>
        <span className="problem-list__num problem-list__open">
            {group.open > 0 ? group.open.toLocaleString('nl-NL') : '–'}
        </span>
        <span className={`problem-list__num${group.new > 0 ? ' problem-list__new' : ''}`}>
            {group.new > 0 ? group.new.toLocaleString('nl-NL') : '–'}
        </span>
        <span className="problem-list__when" title={dateAgo(group.lastFailedAt)}>
            {ago(group.lastFailedAt) || '–'}
        </span>
        <span className="problem-list__when" title={dateAgo(group.firstFailedAt)}>
            {group.open > 0 ? age(group.firstFailedAt) : '–'}
        </span>
    </button>
);

/**
 * What can be done with the kind of problem chosen: look into it, give it to the skill, close
 * it. With where its open issues stand and what became of the earlier ones.
 */
const GroupActions = ({
    group,
    busy,
    copied,
    backlog,
    maxAgeDays,
    onCopy,
    onInvestigate,
    onStopInvestigation,
    onClose,
}: {
    group: IssueGroup;
    busy?: boolean;
    copied: boolean;
    backlog: boolean;
    maxAgeDays: number;
    onCopy: () => void;
    onInvestigate?: () => void;
    onStopInvestigation?: () => void;
    onClose?: () => void;
}) => {
    const o = group.outcomes;
    const facts: { label: string; value: string }[] = [
        { label: 'Open', value: group.open > 0 ? `${group.open.toLocaleString('nl-NL')} (${breakdown(group)})` : 'niets' },
        { label: 'Afgehandeld', value: group.resolved > 0 ? `${o.resolved.toLocaleString('nl-NL')} opgelost en ${o.closed.toLocaleString('nl-NL')} gesloten` : 'nog niets' },
    ];

    if (o.requeued > 0) {
        facts.push({ label: 'Opnieuw aanbieden', value: `lukte ${share(o.requeueResolved, o.requeued)}% (${o.requeueResolved} van ${o.requeued})` });
    }

    if (o.notified > 0) {
        facts.push({ label: 'Na klantmail', value: `${share(o.notifiedResolved, o.notified)}% opgelost (${o.notifiedResolved} van ${o.notified})` });
    }

    if (o.closed > 0 && o.closedKind) {
        facts.push({ label: 'Meest gesloten als', value: CLOSE_KINDS[o.closedKind] ?? o.closedKind });
    }

    return (
        <div className="group-actions">
            <dl className="group-actions__facts">
                {facts.map((f) => (
                    <div key={f.label}>
                        <dt>{f.label}</dt>
                        <dd>{f.value}</dd>
                    </div>
                ))}
            </dl>
            {group.investigation && (
                <p className="group-actions__investigation">
                    <FontAwesomeIcon icon="magnifying-glass" /> In onderzoek bij <strong>{group.investigation.startedBy ?? 'iemand'}</strong> sinds{' '}
                    {age(group.investigation.startedAt)}.{group.investigation.note ? ` ${group.investigation.note}` : ''}
                </p>
            )}
            <div className="issues__actions">
                {onInvestigate && !group.investigation && (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={busy}
                        onClick={onInvestigate}
                        title="Zet dit probleem op 'in onderzoek' en kopieert het voor de skill c2e-research-error"
                    >
                        <FontAwesomeIcon icon="magnifying-glass" /> {copied ? 'Gekopieerd, plak in Claude Code' : 'Onderzoek dit'}
                    </button>
                )}
                {(group.investigation || !onInvestigate) && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCopy} title="Dit probleem met voorbeelden als JSON voor de skill c2e-research-error">
                        <FontAwesomeIcon icon="copy" /> {copied ? 'Gekopieerd' : 'Kopieer voor de skill'}
                    </button>
                )}
                {onStopInvestigation && group.investigation && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={onStopInvestigation}>
                        <FontAwesomeIcon icon="check" /> Onderzoek afronden
                    </button>
                )}
                {onClose && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={busy}
                        onClick={onClose}
                        title={
                            backlog
                                ? `Sluit de open issues van dit probleem met een eerste fout van langer dan ${maxAgeDays} dagen geleden`
                                : 'Sluit alle open issues van dit probleem. Je ziet eerst hoeveel het er zijn.'
                        }
                    >
                        <FontAwesomeIcon icon="box-archive" /> {backlog ? 'Sluit de achterstand' : 'Sluit alle open'}
                    </button>
                )}
            </div>
        </div>
    );
};

const IssueRows = ({
    issue,
    open,
    busy,
    oneEnvironment,
    onToggle,
    onClose,
    onReopen,
    onRelink,
    onLink,
    onMail,
    copied,
    onCopy,
    investigation,
    onInvestigate,
    scanner,
}: {
    issue: Issue;
    open: boolean;
    scanner?: SweeperStatus | null;
    busy?: boolean;
    oneEnvironment?: boolean;
    copied?: boolean;
    onCopy?: () => void;
    investigation?: IssueInvestigation | null;
    onInvestigate?: () => void;
    onToggle: () => void;
    onClose?: (items: IssueRef[], what: string) => void;
    onReopen?: (items: IssueRef[]) => void;
    onRelink?: (issue: Issue) => void;
    /** Opens "Koppelen": the document next to the booking Exact has of it already. */
    onLink?: (issue: Issue) => void;
    onMail?: (issue: Issue) => void;
}) => {
    return (
        <>
            <tr className="is-clickable" onClick={onToggle}>
                <td>
                    <span className={`issues__status issues__status--${issue.status}`}>{STATUS_LABEL[issue.status] ?? issue.status}</span>
                </td>
                {!oneEnvironment && (
                    <td>
                        {issue.customer} / {issue.environment}
                    </td>
                )}
                <td>
                    <span className="issues__id">
                        <IdChip id={issue.relationId} system={EXACT_SIDE.includes(issue.objectType) ? 'exact' : 'chargebee'} />
                    </span>
                    <span className="issues__type">{issue.objectType}</span>
                    {issue.invoiceId && issue.invoiceId !== issue.relationId && (
                        <span className="issues__type">
                            Factuur <IdChip id={issue.invoiceId} system="chargebee" />
                        </span>
                    )}
                </td>
                <td>
                    <FontAwesomeIcon icon={open ? 'angle-down' : 'angle-right'} />{' '}
                    <span className={issue.unrecognised ? 'issues__pattern' : undefined}>{issue.title}</span>
                    {issue.status === 'WaitingOnCustomer' && issue.customerNotifiedAt && (
                        <span className="issues__type">
                            <FontAwesomeIcon icon="envelope" /> Gemeld op {date(issue.customerNotifiedAt)}
                        </span>
                    )}
                </td>
                <td className="issues__num">{issue.attempts}</td>
                <td className="issues__when" title={ago(issue.firstFailedAt)}>{dateTime(issue.firstFailedAt)}</td>
                <td className="issues__when" title={ago(issue.lastFailedAt)}>{dateTime(issue.lastFailedAt)}</td>
                <td>
                    {issue.status === 'Closed'
                        ? `gesloten ${date(issue.closedAt)}`
                        : issue.status === 'Resolved'
                          ? issue.resolution ?? date(issue.resolvedAt)
                          : age(issue.firstFailedAt)}
                </td>
            </tr>
            {open && (
                <tr>
                    <td colSpan={oneEnvironment ? 7 : 8}>
                        <IssueDetail
                            issue={issue}
                            busy={busy}
                            copied={copied}
                            onCopy={onCopy}
                            onClose={onClose}
                            onReopen={onReopen}
                            onRelink={onRelink}
                            onLink={onLink}
                            onMail={onMail}
                            investigation={investigation}
                            onInvestigate={onInvestigate}
                            scanner={scanner}
                        />
                    </td>
                </tr>
            )}
        </>
    );
};
